import { conversationsQuery } from '@cigna/omni/conversations-state';
import { liveAvailabilityQuery } from '@cigna/omni/live-availability-state';
import {
  generateCommChannelCHEContent,
  generateCommChannelEAPContent,
  generateCommChannelSOContent,
  generateCommChannelENContent,
  isAsyncCHEConversation,
  isBehavioralContextTags,
  isClinicalNursingContextTags,
  isDMContextTags,
  isMedicalContextTags,
} from '@cigna/omni/shared';
import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import {
  ChatState,
  ChatStateButton,
  HeaderBackgroundClass,
  HeaderTitle,
} from './chat.interfaces';
import { ConversationDTO } from '@cigna/vampire-dto';
import { CHAT_FEATURE_KEY } from './chat.reducers';
// TODO: https://git.sys.cigna.com/angular-guild/cigna-nx/issues/567
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { featuresEnabled } from '@cigna/features';

export const getChat = createFeatureSelector<ChatState>(CHAT_FEATURE_KEY);

export const getCurrentComponent = createSelector(getChat, ({ history }) =>
  history.length ? history[history.length - 1] : 'home',
);

export const getMuteState = createSelector(getChat, ({ mute }) => mute);

export const getHistoryState = createSelector(
  getChat,
  ({ history }) => history,
);

export const getChatState = createSelector(
  getChat,
  ({ chatState }) => chatState,
);

export const isChatClosed = createSelector(
  getChat,
  ({ chatState }) => chatState === 'closed',
);

export const isChatOpen = createSelector(
  getChat,
  ({ chatState }) => chatState === 'open',
);

export const isChatMinimized = createSelector(
  getChat,
  ({ chatState }) => chatState === 'minimized',
);

export const getDirection = createSelector(
  getChat,
  ({ direction }) => direction,
);

export const getCanGoBack = createSelector(
  getChat,
  isChatMinimized,
  ({ history }, isMinimized) => !!history.length && !isMinimized,
);

export const isBackButtonDisabled = createSelector(
  conversationsQuery.selectConversation,
  conversationsQuery.selectConversationEvents,
  (conversation, events) =>
    !!conversation &&
    ((conversation.type === 'bot' && !events.length) ||
      conversation.state === 'transfer'),
);

export const getBackButtonContent = createSelector(
  conversationsQuery.selectConversation,
  (conv) => {
    if (conv && conv.state !== 'closed' && conv.type !== 'async') {
      return 'End Chat';
    }
    return 'Back';
  },
);

export const getHeaderBackgroundClass = createSelector(
  getCurrentComponent,
  (component): HeaderBackgroundClass =>
    component === 'home' ? '' : 'white-header',
);

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getHeaderTitleByConversationType(
  type: ConversationDTO['type'],
  state: ConversationDTO['state'],
  liveAvailability: boolean,
  context: ConversationDTO['context'],
  enableNewChatExp: boolean,
) {
  const newChatTitle = 'Chat With Us';
  switch (type) {
    case 'live':
      return state !== 'closed'
        ? enableNewChatExp
          ? newChatTitle
          : 'Chat'
        : 'Closed Chat';
    case 'async':
      if (state !== 'closed') {
        return isBehavioralContextTags(context.tags) ||
          isMedicalContextTags(context.tags)
          ? 'Direct Message'
          : 'Message';
      }
      return isBehavioralContextTags(context.tags) ||
        isMedicalContextTags(context.tags)
        ? 'Closed Direct Message'
        : 'Closed Message';
    case 'bot':
      return liveAvailability && !isClinicalNursingContextTags(context.tags)
        ? enableNewChatExp
          ? newChatTitle
          : 'Chat'
        : 'Message';
    default:
      return enableNewChatExp ? newChatTitle : '';
  }
}

export const getHeaderTitle = createSelector(
  getCurrentComponent,
  conversationsQuery.selectConversation,
  liveAvailabilityQuery.getLiveChatAvailable,
  featuresEnabled(['enableNewPreChatWelcome']),
  (
    component,
    conversation,
    liveAvailability,
    enableNewPreChatWelcome,
  ): HeaderTitle => {
    switch (component) {
      case 'dialog':
        if (!conversation) {
          return enableNewPreChatWelcome ? 'Chat With Us' : '';
        }

        return getHeaderTitleByConversationType(
          conversation.type,
          conversation.state,
          liveAvailability,
          conversation.context,
          enableNewPreChatWelcome,
        );

      case 'history':
        return enableNewPreChatWelcome ? 'Chat History' : 'My Conversations';
      default:
        return '';
    }
  },
);

export const shouldClose = createSelector(
  getCurrentComponent,
  conversationsQuery.selectConversation,
  isChatMinimized,
  (component, conversation, minimized) =>
    minimized
      ? false
      : !(
          component === 'dialog' &&
          conversation &&
          conversation.state !== 'closed' &&
          (conversation.type === 'live' ||
            conversation.type === 'bot' ||
            isDMContextTags(conversation.context.tags) ||
            isAsyncCHEConversation(conversation.context, conversation.type))
        ),
);

export const toggleChatStateButton: MemoizedSelector<object, ChatStateButton> =
  createSelector(isChatOpen, shouldClose, (isOpen, close): ChatStateButton => {
    if (close) {
      return {
        copy: 'Close',
        icon: 'close',
        iconData: {},
      };
    }

    return isOpen
      ? {
          copy: 'Minimize',
          icon: 'directionArrow',
          iconData: {
            direction: 'bottom',
          },
        }
      : {
          copy: 'Open',
          icon: 'directionArrow',
          iconData: {
            direction: 'top',
          },
        };
  });

export const getCommChannelContent = createSelector(
  featuresEnabled(['asyncSupportChat']),
  featuresEnabled(['liveClinicianChat']),
  liveAvailabilityQuery.getLiveChatAvailable,
  liveAvailabilityQuery.getChatCloseHrsMsg,
  featuresEnabled(['personalGuideChat']),
  featuresEnabled(['advocateChat']),
  featuresEnabled(['isEvernorthEnabled']),
  (
    isAsyncSupportEnabled,
    isLiveClinicianChatEnabled,
    isLiveChatAvailable,
    chatCloseHrsMsg,
    isPersonalGuide,
    advocateChat,
    isEvernorthEnabled,
  ) => ({
    support: {
      ...generateCommChannelSOContent({
        isAsyncSupportEnabled,
        isLiveChatAvailable,
        isPersonalGuide,
      }),
    },
    che: isLiveClinicianChatEnabled
      ? {
          ...generateCommChannelCHEContent(isLiveChatAvailable),
        }
      : null,
    eap:
      advocateChat && !isEvernorthEnabled
        ? {
            ...generateCommChannelEAPContent(isLiveChatAvailable),
          }
        : advocateChat && isEvernorthEnabled
        ? {
            ...generateCommChannelENContent(
              isLiveChatAvailable,
              chatCloseHrsMsg,
            ),
          }
        : null,
  }),
);

export const isHistory = createSelector(
  getHistoryState,
  (historyState): boolean => historyState.includes('history'),
);

export const chatQuery = {
  getBackButtonContent,
  getCanGoBack,
  getChatState,
  getChat,
  getCommChannelContent,
  getCurrentComponent,
  getDirection,
  getHeaderBackgroundClass,
  getHeaderTitle,
  isBackButtonDisabled,
  isChatClosed,
  isChatOpen,
  isChatMinimized,
  shouldClose,
  toggleChatStateButton,
  getMuteState,
  isHistory,
};
